import React, { FC, Fragment } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { getPublishedReleaseNotesTemplate } from 'apollo/templates/release-notes';
import { ReleaseNote } from 'models/release-note';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { formatDate, getShortLanguageCode } from 'utils/format';
import ReleaseNoteContent from 'components/release-notes/release-note-content/release-note-content.component';
import { resolveOrgType, resolveContent } from 'utils/release-note-utils';

const ReleaseNotesHistory: FC = () => {
    const { t: translate } = useTranslation(['navigation', 'release-notes']);
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();

    const organizationType = resolveOrgType(currentOrg.type);
    const userLanguage = getShortLanguageCode(currentUser.localeFormat?.language);

    const { data, loading } = useQuery<{ publishedReleaseNotes: ReleaseNote[] }>(getPublishedReleaseNotesTemplate, {
        variables: { organizationType },
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });

    const releaseNotes = data?.publishedReleaseNotes ?? [];

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('navigation:RELEASE_NOTES')} />

            {loading && releaseNotes.length === 0 ? (
                <Loader />
            ) : releaseNotes.length === 0 ? (
                <Typography color="text.secondary">{translate('release-notes:NO_RELEASE_NOTES')}</Typography>
            ) : (
                <Box>
                    {releaseNotes.map((releaseNote, noteIndex) => {
                        const content = resolveContent(releaseNote, userLanguage);
                        const isLatest = noteIndex === 0;
                        return (
                            <Accordion key={releaseNote._id} defaultExpanded={isLatest}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`release-note-${releaseNote._id}-content`}
                                    id={`release-note-${releaseNote._id}-header`}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                        <Typography fontWeight={600}>
                                            {translate('release-notes:WHATS_NEW', {
                                                version: releaseNote.version,
                                            })}
                                        </Typography>
                                        {isLatest && (
                                            <Chip
                                                label={translate('release-notes:LATEST')}
                                                color="primary"
                                                size="small"
                                                sx={{ fontWeight: 600 }}
                                            />
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(releaseNote.createdAt, 'date')}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ReleaseNoteContent content={content} />
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            )}
        </Fragment>
    );
};

export default ReleaseNotesHistory;
